/**
 * DigitalCompanionPage — 2026-08-19 (v4: cartoon persona avatars).
 *
 * Overview of this account's Digital Companion: how many personas are
 * configured/active, how many tickets each has handled, and real customer
 * satisfaction numbers. Backed by GET /digital-companion/overview (reuses
 * digo_tools' read functions) + the existing CSAT analytics endpoint
 * (same one CSATAnalyticsPage already uses, not a second CSAT system).
 *
 * Since this account has one companion, not several, each CARD here is
 * one category from the underlying data model, presented to the user as
 * a "persona" — badges are colored/varied by index.
 *
 * v4: each persona shows an original hand-drawn cartoon avatar with a
 * headset + mic (see PersonaAvatar.tsx) — replaced v3's real-3D WebGL
 * snapshots after "everyone has the same model" feedback (that approach
 * reused one CC0 rig recolored/rotated per variant, which read as one
 * person, not distinct personas). No external assets, no async
 * generation step.
 *
 * "Real-time monitoring": polls the overview endpoint every 30s (same
 * interval DashboardLayout already uses for the conversation-count badge)
 * so open-ticket counts and the live pulse move without a manual refresh —
 * stated plainly as periodic polling, not oversold as push-based/websocket.
 *
 * Deliberately does NOT show a "calls handled" number — see
 * backend/app/api/digital_companion.py's module docstring for exactly why
 * (call volume for this pipeline is only ever logged, never persisted to a
 * table). Ticket counts are shown instead, labeled honestly as tickets/
 * issues handled, not calls.
 */
import React, { useEffect, useRef, useState } from 'react';
import { MessageSquareText, ThumbsUp, Layers, Loader2 } from 'lucide-react';
import api from '../services/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import CategoryIconBadge, { hueColorForIndex } from '../components/DigitalCompanion/CategoryIconBadge';

interface CategoryStat {
  id: string;
  label: string | null;
  active: boolean;
  resolution_mode: string | null;
  persona_name: string | null;
  tickets_total: number;
  tickets_open: number;
}

interface VoiceInfo {
  purpose: string;
  provider: string | null;
  voice: string | null;
  enabled: boolean;
  connected: boolean;
}

interface OverviewResponse {
  account_id: number;
  enabled: boolean;
  voice: VoiceInfo | null;
  categories: CategoryStat[];
  totals: {
    categories_active: number;
    categories_total: number;
    tickets_total: number;
    tickets_open: number;
  };
}

interface CSATStatistics {
  total_responses: number;
  average_rating: number;
  satisfaction_rate: number;
}

/** Defensive: category "labels" is a {en, fr, ...} object at the data
 * layer — digo_tools.py extracts a plain string before this ever reaches
 * the API (2026-08-19 fix, after this exact shape crashed React here
 * first: "object with keys {en}"), but this render site shouldn't be able
 * to crash the page again if that ever regresses. */
const categoryLabel = (cat: CategoryStat) => (typeof cat.label === 'string' && cat.label ? cat.label : cat.id);

const POLL_MS = 30_000;

const DigitalCompanionPage: React.FC = () => {
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [csat, setCsat] = useState<CSATStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accountIdRef = useRef<number | null>(null);

  const loadOverview = async (isInitial: boolean) => {
    if (isInitial) setLoading(true);
    try {
      const overviewRes = await api.get<OverviewResponse>('/digital-companion/overview');
      setOverview(overviewRes.data);
      setError(null);

      if (isInitial) {
        accountIdRef.current = overviewRes.data.account_id;
        try {
          const csatRes = await api.get(`/accounts/${overviewRes.data.account_id}/csat/analytics`);
          setCsat(csatRes.data?.statistics ?? null);
        } catch {
          // CSAT is a nice-to-have here — the page still works without it.
          setCsat(null);
        }
      }
    } catch (err: any) {
      if (isInitial) setError(err?.response?.data?.detail || 'Failed to load Digital Companion overview.');
      // Silent on background poll failures — keep showing the last good data
      // rather than replacing a working page with an error on a transient blip.
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview(true);
    const id = setInterval(() => loadOverview(false), POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading Digital Companion overview…
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            {error || 'No data available.'}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { totals } = overview;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Companion hero */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 p-5">
          <CategoryIconBadge
            color={hueColorForIndex(0)}
            variant={0}
            live={overview.enabled}
            size={88}
          />
          <div className="flex-1 min-w-[180px]">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight">Digital Companion</h1>
              <Badge
                variant="outline"
                className={
                  overview.enabled
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                    : 'border-border bg-muted text-muted-foreground'
                }
              >
                {overview.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {overview.voice?.provider
                ? `${overview.voice.provider} — ${overview.voice.voice ?? 'default voice'}`
                : 'No voice configured yet'}
              {overview.voice && !overview.voice.connected ? ' · not connected' : ''}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">Updates every 30s</p>
        </CardContent>
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Personas active</CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">
              {totals.categories_active}
              <span className="text-base font-normal text-muted-foreground"> / {totals.categories_total}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Tickets handled</CardTitle>
            <MessageSquareText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">{totals.tickets_total}</div>
            <p className="text-xs text-muted-foreground">{totals.tickets_open} currently open</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Customer satisfaction</CardTitle>
            <ThumbsUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {csat ? (
              <>
                <div className="text-2xl font-bold tabular-nums">{csat.average_rating.toFixed(1)} / 5</div>
                <p className="text-xs text-muted-foreground">
                  {csat.satisfaction_rate.toFixed(0)}% satisfied · {csat.total_responses} responses
                </p>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No CSAT data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-dashed">
        <CardContent className="p-4 text-xs text-muted-foreground">
          Call volume isn't shown here — it isn't tracked as a stored metric yet (only logged per-call,
          not written to a database table). Ticket counts reflect issues the companion has actually
          handled per persona. Customer satisfaction is account-wide across all channels, not split
          by persona. "Live" below means a persona currently has an open ticket, refreshed every 30s.
        </CardContent>
      </Card>

      {/* Per-persona avatar cards */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-muted-foreground">Personas</h2>
        {overview.categories.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No personas configured yet.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {overview.categories.map((cat, i) => {
              const isLive = cat.active && cat.tickets_open > 0;
              return (
                <Card key={cat.id}>
                  <CardContent className="flex flex-col gap-3 p-4">
                    <div className="flex items-start gap-3">
                      <CategoryIconBadge
                        color={hueColorForIndex(i)}
                        variant={i}
                        live={isLive}
                        size={64}
                      />
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-semibold">{categoryLabel(cat)}</h3>
                        <p className="truncate text-xs text-muted-foreground">
                          {cat.persona_name || 'No persona set'}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          isLive
                            ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300'
                            : cat.active
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : 'border-border bg-muted text-muted-foreground'
                        }
                      >
                        {isLive ? `Live · ${cat.tickets_open}` : cat.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <dl className="grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs">
                      <div>
                        <dt className="text-muted-foreground">Handled</dt>
                        <dd className="font-semibold tabular-nums">{cat.tickets_total}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Open now</dt>
                        <dd className="font-semibold tabular-nums">{cat.tickets_open}</dd>
                      </div>
                    </dl>
                    <p className="text-xs text-muted-foreground">
                      {cat.resolution_mode === 'virtual_close' ? 'Companion resolves directly' : 'Hands off to a human'}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalCompanionPage;
